'use strict';
'use es6';

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getAvatarResolver = exports.getAvatarURI = exports.LAST_TRY_DELAY = exports.SECOND_TRY_DELAY = exports.FIRST_TRY_DELAY = undefined;
exports.normalizeLookup = normalizeLookup;
exports.makeCacheAtom = makeCacheAtom;
exports.makeQueueAtom = makeQueueAtom;
exports.queuer = queuer;
exports.makeQueuer = makeQueuer;
exports.flush = flush;
exports.makeAvatarResolver = makeAvatarResolver;

var _underscore = require('../utils/underscore');

var _atom = require('atom');

var Atom = _interopRequireWildcard(_atom);

var _AvatarAPI = require('./AvatarAPI');

var _defaultAvatarFetcher = require('./defaultAvatarFetcher');

var _defaultAvatarFetcher2 = _interopRequireDefault(_defaultAvatarFetcher);

var _immutable = require('immutable');

var _invariant = require('react-utils/invariant');

var _invariant2 = _interopRequireDefault(_invariant);

var _hubspot = require('hubspot.PortalIdParser');

var _hubspot2 = _interopRequireDefault(_hubspot);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj.default = obj; return newObj; } }

var FIRST_TRY_DELAY = exports.FIRST_TRY_DELAY = 200;
var SECOND_TRY_DELAY = exports.SECOND_TRY_DELAY = 1000;
var LAST_TRY_DELAY = exports.LAST_TRY_DELAY = 4000;

/**
 * Given a lookup string, removes leading/trailing whitespace and lowercases
 * to avoid subtle cache misses.
 *
 * @param  {string}
 * @return {string}
 */
function normalizeLookup(lookup) {
  if (typeof lookup !== 'string') {
    return undefined;
  }
  lookup = lookup.trim().toLowerCase();
  if (lookup.length === 0) {
    return undefined;
  }
  return lookup;
}

/**
 * Given the various state Atoms, retrieves a URI from the cache or adds the
 * lookup to the queue.
 *
 * @param  {Atom<Map<string,?string>>}
 * @param  {Atom<Set<string>>}
 * @param  {Function}
 * @param  {Function}
 * @param  {number}
 * @param  {string}
 * @return {?string}
 */
function _getAvatarURI(cacheAtom, pendingAtom, enqueue, fetcher, portalId, lookup) {
  (0, _invariant2.default)(typeof fetcher === 'function', 'expected `fetcher` to be a function but got `%s`', fetcher);
  (0, _invariant2.default)(typeof portalId === 'number', 'expected `portalId` to be a number but got `%s`', portalId);
  var key = normalizeLookup(lookup);
  if (!key) {
    return null;
  }
  var avatars = Atom.deref(cacheAtom);
  if (avatars.has(key)) {
    return avatars.get(key);
  }
  if (!Atom.deref(pendingAtom).has(key)) {
    enqueue(fetcher, portalId, _immutable.Set.of(key));
  }
  return undefined;
}

/**
 * @param  {?Map<string,?string>}
 * @return {Atom<Map<string,?string>>}
 */
exports.getAvatarURI = _getAvatarURI;
function makeCacheAtom() {
  var initialCache = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : (0, _immutable.Map)();

  return Atom.atom(initialCache, _immutable.Map.isMap);
}

/**
 * @param  {?Set<string>}
 * @return {Atom<Set<string>>}
 */
function makeQueueAtom() {
  var initialQueue = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : (0, _immutable.Set)();

  return Atom.atom(initialQueue, _immutable.Set.isSet);
}

/**
 * Adds keys to the queue and schedules a queue flush.
 *
 * @param  {Atom<Set<string>>}
 * @param  {Function}
 * @param  {Function}
 * @param  {number}
 * @param  {Set<string>}
 * @return {void}
 */
function queuer(queueAtom, flushQueue, fetcher, portalId, keys) {
  (0, _invariant2.default)(_immutable.Set.isSet(keys) && !keys.isEmpty(), 'expected `keys` to be a non-empty Set but got `%s`', keys);
  Atom.swap(queueAtom, function (current) {
    return current.union(keys);
  });
  flushQueue(fetcher, portalId);
}

/**
 * Creates a queing function bound to a queue Atom.
 *
 * @param  {Function}
 * @param  {Function}
 * @param  {Atom<Map<string,?string>>}
 * @param  {Atom<Set<string>>}
 * @param  {Function}
 * @return {Function}
 */
function makeQueuer(doEnqueue, doFlush, cacheAtom, pendingAtom, nextEnqueue) {
  var queueAtom = makeQueueAtom();
  var flushQueue = (0, _underscore.partial)(doFlush, cacheAtom, queueAtom, pendingAtom, nextEnqueue);
  return (0, _underscore.partial)(doEnqueue, queueAtom, flushQueue);
}

/**
 * Try to resolve the contents of a queue, adding hits to the cache and pushing
 * retries to the next queue.
 *
 * @param  {Atom<Map<string,?string>>}
 * @param  {Atom<Set<string>>}
 * @param  {Atom<Set<string>>}
 * @param  {Function}
 * @param  {Function}
 * @param  {number}
 * @return {void}
 */
function flush(cacheAtom, queueAtom, pendingAtom, nextFlush, fetcher, portalId) {
  var keys = Atom.deref(queueAtom);
  if (keys.isEmpty()) {
    return;
  }
  Atom.swap(pendingAtom, function (current) {
    return current.union(keys);
  });
  Atom.swap(queueAtom, function (current) {
    return current.clear();
  });
  (0, _AvatarAPI.fetchAvatars)(fetcher, portalId, keys).then(function (_ref) {
    var retry = _ref.retry,
        updates = _ref.updates;

    Atom.swap(pendingAtom, function (current) {
      return current.subtract(keys);
    });
    Atom.swap(cacheAtom, function (current) {
      return current.merge(updates);
    });
    if (nextFlush && !retry.isEmpty()) {
      nextFlush(fetcher, portalId, retry);
    }
  }).done();
}

/**
 * Factory that creates the various state Atoms and bound functions that compose
 * an AvatarResolver and cache.
 *
 * @return {Object}
 */
function makeAvatarResolver() {
  var cacheAtom = makeCacheAtom();
  var pendingAtom = makeQueueAtom();

  var enqueueLastTry = makeQueuer(queuer, (0, _underscore.debounce)(flush, LAST_TRY_DELAY), cacheAtom, pendingAtom);

  var enqueueSecondTry = makeQueuer(queuer, (0, _underscore.debounce)(flush, SECOND_TRY_DELAY), cacheAtom, pendingAtom, enqueueLastTry);

  var enqueueFirstTry = makeQueuer(queuer, (0, _underscore.debounce)(flush, FIRST_TRY_DELAY), cacheAtom, pendingAtom, enqueueSecondTry);

  return {
    /**
     * The Atom that stores the lookup => url cache. It's usually best to use
     * the helper methods (getAvatarURI, setAvatarURI, etc.) rather than using
     * the cache directly.
     *
     * @prop  {Atom<Map<string,?string>>}
     */
    avatars: cacheAtom,

    /**
     * Returns an avatar uri from the cache or queues the lookup to be fetched.
     *
     * @param  {string}    lookup (email or domain)
     * @param  {Function}  fetcher that returns a promise for the API response
     * @param  {number}    portal id
     * @return {?string}   avatar url
     */
    getAvatarURI: function getAvatarURI(lookup) {
      var fetcher = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : _defaultAvatarFetcher2.default;
      var portalId = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : _hubspot2.default.get();

      return _getAvatarURI(cacheAtom, pendingAtom, enqueueFirstTry, fetcher, portalId, lookup);
    },


    /**
     * Associate an avatar uri with a lookup in the cache. This is useful when
     * client interaction changes the image an avatar should display (i.e. the
     * user uploaded a new profile photo for a contact).
     *
     * @param  {string}    lookup (email or domain)
     * @param  {?string}   new url or null
     * @param  {Function}  fetcher that returns a promise for the API response
     * @param  {number}    portal id
     * @return {?string}   avatar url
     */
    setAvatarURI: function setAvatarURI(lookup, value) {
      var key = normalizeLookup(lookup);
      (0, _invariant2.default)(key !== undefined, 'expected `lookup` to be a non-empty string but got `%s`', lookup);
      (0, _invariant2.default)(value === null || typeof value === 'string' && value.length > 0, 'expected `value` to be a non-empty string or `null` but got `%s`', value);
      Atom.swap(cacheAtom, function (current) {
        return current.set(key, value);
      });
    },


    /**
     * Register a callback to be triggered whenever the avatars caches changes.
     *
     * @param  {Function}  callback
     * @return void
     */
    watch: (0, _underscore.partial)(Atom.watch, cacheAtom),

    /**
     * Unregister a callback that was previously registered with `watch`.
     *
     * @param  {Function}  callback
     * @return void
     */
    unwatch: (0, _underscore.partial)(Atom.unwatch, cacheAtom)
  };
}

/**
 * Returns the AvatarResolver singleton used by UIAvatar.
 *
 * @return {Object}
 */
var getAvatarResolver = exports.getAvatarResolver = (0, _underscore.once)(makeAvatarResolver);


//////////////////
// WEBPACK FOOTER
// /usr/share/hubspot/build/.hubspot/static-archive/UIComponents/static-2.348/js/avatar/AvatarResolver.js
// module id = 224
// module chunks = 0 1